import React from "react";

import { connect } from 'react-redux';
import { navigateTop } from '../routing/router-utils';
/* redux */
import { setBasicInfo } from '../actions/registrationActions';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import AddressUpdater from '../address/AddressUpdater';
import { validateSignUpEntry, isValidPassword, isValidEmail } from './validation-utils';
import { getBackLink, checkEmailAvailability, checkPasswordStrength } from './registration-utils';
import { errMsg } from './registration-errors';
import RegFlowHeader from "./RegFlowHeader";

/* Material UI overrides */
import './MaterialUI.module.css';
import styles from './Registration.module.css';

class SignUp extends React.Component {
  
  constructor(props) {
    super();

    const dob = props.dob || {};
    const location = props.location || {};
    
    // must exactly match input names 
    this.state = {
      name: props.name,
      nameErr: false,
      email: props.email,
      emailErr: false,
      password: props.password,
      passwordErr: false,
      /* location - mostly moved to AddressUpdater */
      
      apartment_number: location.apartment_number,
      /* date of birth */
      month: dob.month,
      monthErr: false,
      day: dob.day,
      dayErr: false,
      year: dob.year,
      yearErr: false,
      /* Must be initialized to boolean or there'll be controlled/uncontrolled input errors */
      accept_terms: props.accept_terms || false, 
      accept_termsError: false,
      accept_marketing: props.accept_marketing || true,
      hasValidationError: false,
      emailAvailable: null,
      isStrongPassword: null,
      backLink: null
    };
  }
 
  componentDidMount() {
    // determine the header back link
    const backLink = getBackLink();
    const returnTo = this.props.return_to_path;
    // backLink is either the /address page or the 'returnTo' path (a page outside of the reg flow)
    this.setState({
      backLink: backLink || returnTo || '/'
    });  
  }

  componentDidUpdate() {
    if (this.state.hasValidationError) {
      // scroll to first invalid item
      const errorElem = document.querySelector('.Mui-error');
      if (errorElem) {
        window.scrollBy(0, errorElem.getBoundingClientRect().top - 48); // 3 rem
        this.setState({hasValidationError: false});
      } 
    }
  }

  render() {
  
    /**
     * Validate a single form input.
     * 
     * @param string item
     */
    const isValidEntry = (regItem) => {
      const isValid = validateSignUpEntry(regItem, this.state[regItem]);
      const stateJson = JSON.parse(`{ "${regItem}Err" : ${!isValid} }`); 
      this.setState(stateJson);
      return isValid;
    }; 

    const handleEmailOnBlur = (emailAvailable) => {
      if (isValidEmail(this.state.email)) {
        this.setState({ 
          emailAvailable,
          emailErr: !emailAvailable      
        });
      } else {
        // suppress availability message
        this.setState({
          emailAvailable: null, 
          emailErr: true
        });
      }   
    };

    const handlePasswordOnBlur = (isStrongPassword) => {
      this.setState({ 
        isStrongPassword,
        passwordErr: !isStrongPassword      
      });
    };

    const getPasswordErrorMsg = (password) => {
      if (isValidPassword(password)) {
        return '';
      } else if (password.length < 8) {
        return '\u26A0 Minimum length: 8 characters';
      } else {
        return '\u26A0 Please use uppercase, lowercase and number(s)';
      }
    }; 

    /**
     * Validate all required form inputs
     */
    const validate = () => {
      const validName = isValidEntry('name');
      const validEmail = isValidEntry('email');
      const validPassword = isValidEntry('password');
      
      const validMonth = isValidEntry('month');
      const validDay = isValidEntry('day');
      const validYear = isValidEntry('year');
      const validAcceptTerms = isValidEntry('accept_terms');

      const isValidData = validName && validEmail && validPassword &&
                          validMonth && validDay && validYear && validAcceptTerms; 
      this.setState({hasValidationError: !isValidData});
      return isValidData; 
    };  

    const {
      name,
      nameErr,
      email,
      emailErr,
      password,
      passwordErr,
      apartment_number,
      month,
      monthErr,
      day,
      dayErr,
      year,
      yearErr,
      accept_terms,
      accept_termsErr,
      accept_marketing
    } = this.state;

    /**
     * Update state with the form input values
     * 
     * NOTE: The way I have this, the state item names MUST match the form input names:
     *   "state.email" will update from <input id="email"  
     * 
     * @param {*} event 
     */
    const onChange = event => {
      let stateJson;
      if (event.currentTarget.type === "checkbox") {
        const { id, checked:value } = event.currentTarget;
        stateJson = JSON.parse(`{ "${id}" : ${value} }`); 
      } else {
        const { id, value } = event.currentTarget;
        stateJson = JSON.parse(`{ "${id}" : "${value}" }`); 
      }  
      this.setState(stateJson);
      if (stateJson.zip_code) {
        this.updateStorageZipCode(stateJson['zip_code']);
      }
    }     
   
    /**
     *  Update redux with values 
     */
    const handleSubmit = (event) => {
      event.preventDefault();

      const valid = validate();
      if (!valid) {
        return;
      }
      // push data to Redux
      this.props.setBasicInfo({ 
        name,
        email,
        password,  
        apartment_number,
        dob: { day, month, year },
        accept_terms,
        accept_marketing
      });
      navigateTop('/signUp/phoneNumber'); 
    };

    /* Material UI TextField Variant */
    const tF = 'filled'; 

    return (
      <div className={styles.formOnlyPage}>
        {/* 
          * This is the first ( or second ) page of the reg flow so the 'backLink'
          * returns the user to the page they entered from (checkout etc.) or the address
          * input page
          */}
        <RegFlowHeader backLink={this.state.backLink} pageTitle="Personal Info" />

        <div className={styles.regFlowForm}>
          <h1 className={`${styles.formHeader} ${styles.formHeaderWide}`}>
            Sign Up
          </h1>
          
          <form>
            {/* Name */}
            <TextField id="name" value={name} label="Full Name" error={nameErr} helperText={errMsg('name',nameErr)} 
                       autoFocus variant={tF} onChange={onChange} />
            
            {/* Display email availability notice after value is set true/false */}
            <div className={styles.inputFeedback}>
              { this.state.emailAvailable === true && !this.state.emailErr &&
                <div className={styles.inputSuccess}>&#10003; Account available</div>
              }
              { this.state.emailAvailable === false &&
                <div className={styles.inputFailure}>&#9888; An account exists for this email address</div>
              }
            </div>
  
            {/* Email */}
            <TextField id="email" value={email} label="Email" error={emailErr} helperText={errMsg('email',emailErr)} 
                       onBlur={() => checkEmailAvailability(this.state.email, emailAvailable => handleEmailOnBlur(emailAvailable))}
                       variant={tF}  onChange={onChange} />
            
            {/* Display password strength notice after value is set true/false */}
            <div className={styles.inputFeedback}>
              { this.state.isStrongPassword === true &&
                <div className={styles.inputSuccess}>&#10003; Strong password</div>
              }
              { this.state.isStrongPassword === false &&
                <div className={styles.inputFailure}>{ getPasswordErrorMsg(this.state.password) }</div>
              }
            </div>
            
            {/* Password */}
            <TextField id="password" type="password" value={password} error={passwordErr} helperText={errMsg('password',passwordErr)} 
                      onBlur={() => checkPasswordStrength(this.state.password, isStrongPassword => handlePasswordOnBlur(isStrongPassword))} 
                      label="Password" variant={tF}
                      onChange={onChange} />

            <AddressUpdater />
          
            {/* Apartment */}
            <TextField id="apartment_number" value={apartment_number} label="Apartment Number (optional)" variant={tF} onChange={onChange} /> 
          
            {/* DOB */}
            <div className={styles.calWrap}>
              <div>Date of Birth:</div>
              <div className={styles.calEntries}>
                <TextField id="month" value={month} error={monthErr} helperText={errMsg('month', monthErr)} 
                           inputProps={{ maxLength:2, pattern:"[0-9]*" }} label="Month" variant={tF} onChange={onChange} />
                <TextField id="day" value={day} error={dayErr} helperText={errMsg('day', dayErr)} 
                           inputProps={{ maxLength:2, pattern:"[0-9]*" }} label="Day" variant={tF} onChange={onChange} />
                <TextField id="year" value={year} error={yearErr} helperText={errMsg('year', yearErr)} 
                           inputProps={{ maxLength:4, pattern:"[0-9]*" }} label="Year" variant={tF} onChange={onChange} />
              </div> 
            </div>
            
            <div id="terms" className={styles.termsBox}>
              <FormControlLabel 
                control={<Checkbox 
                  color="default" 
                  id="accept_terms" 
                  checked={accept_terms} 
                  onChange={onChange} />}
                label="I hereby agree to the Zip Run Terms of Service and Privacy&nbsp;Policy (required)"
              />
            </div>
            
            { accept_termsErr && (
              <div className={styles.termsError}>
                { errMsg('accept_terms', true) }
              </div>
            )}
  
            <div className={styles.termsBox}>
              <FormControlLabel 
                control={<Checkbox 
                  color="default" 
                  id="accept_marketing" 
                  checked={accept_marketing} 
                  onChange={onChange} />}
                label="Yes, send me updates and promotions via email or SMS message (you can update this in your account settings)"
              />
            </div>  

            <button 
              className={`${styles.formButton} ${styles.formSubmit}`}
              onClick={handleSubmit}>
              Sign Up
            </button>
          </form>
        </div>
      </div>
    );
  }  
};

const mapStateToProps = (state)=>{
  return {
    name: state.name,
    email: state.email,
    password: state.password,
    apartment_number: state.apartment_number,
    dob: state.dob,
    accept_terms: state.terms_accepted,
    accept_marketing: state.marketing_accepted,
    return_to_path: state.return_to_path
  }
}

const mapDispatchToProps = (dispatch)=>{
  return {
    setBasicInfo: (infoObj)=>{dispatch(setBasicInfo(infoObj))}
  }
}

export default connect(mapStateToProps,mapDispatchToProps)(SignUp);
